/**
 * NavArrows - Flechas de navegación lateral flotantes
 * 
 * Flechas izquierda/derecha fijas en los laterales de la pantalla
 * para navegar entre pasos del wizard. Independientes del scroll.
 * 
 * @version 1.3.0 - Añadida animación pulse-glow infinita para indicar disponibilidad
 */

import { useState, type FC, type CSSProperties } from 'react';
import { colors, typography } from '@/design-system/v2';

// ============================================================================
// DESIGN TOKENS (DS v2)
// ============================================================================

const COLORS = {
  bgSecondary: colors.bg.primary,
  bgTertiary: colors.bg.secondary,
  fgMuted: colors.fg.muted,
  fgSecondary: colors.fg.secondary,
  fgDisabled: colors.fg.subtle,
  primary: colors.primary,
  border: colors.border.DEFAULT,
  borderDisabled: colors.border.DEFAULT,
} as const;

const STEP_LABELS: Record<number, string> = {
  1: 'Normaliza',
  2: 'Valida',
  3: 'Coteja',
  4: 'Exporta',
};

// ============================================================================
// TYPES
// ============================================================================

export interface NavArrowsProps {
  /** Paso actual (1, 2, 3, 4) */
  currentStep: 1 | 2 | 3 | 4;
  /** Si el paso actual está completado y se puede avanzar */
  canAdvance?: boolean;
  /** Activar animación pulse-glow en flecha Next */
  animateNext?: boolean;
  /** Callback al ir al paso anterior */
  onPrev?: () => void;
  /** Callback al ir al paso siguiente */
  onNext?: () => void;
}

// ============================================================================
// STYLES
// ============================================================================

const arrowBaseStyle: CSSProperties = {
  position: 'fixed',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  padding: '8px 4px',
  background: 'transparent',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: typography.fontFamily.sans,
};

const arrowLeftStyle: CSSProperties = {
  ...arrowBaseStyle,
  left: '12px',
};

const arrowRightStyle: CSSProperties = {
  ...arrowBaseStyle,
  right: '12px',
};

const _iconStyle: CSSProperties = {
  width: 24,
  height: 56,
};

const _labelStyle: CSSProperties = {
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  writingMode: 'vertical-rl',
  textOrientation: 'mixed',
};

const tooltipStyle: CSSProperties = {
  position: 'absolute',
  right: '100%',
  top: '50%',
  transform: 'translateY(-50%)',
  marginRight: '8px',
  padding: '6px 10px',
  background: COLORS.bgTertiary,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: 500,
  color: COLORS.fgMuted,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
};

// ============================================================================
// ICONS
// ============================================================================

const ChevronLeft: FC = () => (
  <svg viewBox="0 0 24 24" width={26} height={48} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight: FC = () => (
  <svg viewBox="0 0 24 24" width={26} height={48} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

// Keyframes para animación pulse-glow (inyectados una vez)
const PULSE_GLOW_KEYFRAMES = `
@keyframes pulse-glow-nav {
  0%, 100% {
    border-color: rgba(45, 212, 191, 0.15);
    background: transparent;
  }
  50% {
    border-color: rgba(45, 212, 191, 0.5);
    background: rgba(45, 212, 191, 0.06);
  }
}
`;

// Inyectar keyframes en el documento (una sola vez)
let stylesInjected = false;
function injectPulseGlowStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = PULSE_GLOW_KEYFRAMES;
  document.head.appendChild(style);
  stylesInjected = true;
}

export const NavArrows: FC<NavArrowsProps> = ({
  currentStep,
  canAdvance = true,
  animateNext = false,
  onPrev,
  onNext,
}) => {
  // Inyectar estilos de animación
  if (animateNext) {
    injectPulseGlowStyles();
  }
  const [hoveredLeft, setHoveredLeft] = useState(false);
  const [hoveredRight, setHoveredRight] = useState(false);

  // Derivar estados de navegación
  const canGoBack = currentStep > 1;
  const canGoForward = currentStep < 4;
  const isNextDisabled = canGoForward && !canAdvance;
  const prevLabel = canGoBack ? STEP_LABELS[currentStep - 1] : '';
  const nextLabel = canGoForward ? STEP_LABELS[currentStep + 1] : '';

  const getLeftStyle = (): CSSProperties => ({
    ...arrowLeftStyle,
    color: hoveredLeft ? COLORS.primary : COLORS.fgMuted,
    borderColor: hoveredLeft ? COLORS.primary : COLORS.border,
    background: hoveredLeft ? COLORS.bgTertiary : COLORS.bgSecondary,
    opacity: canGoBack ? 1 : 0,
    pointerEvents: canGoBack ? 'auto' : 'none',
    visibility: canGoBack ? 'visible' : 'hidden',
  });

  const getRightStyle = (): CSSProperties => {
    // Estado disabled: mostrar pero no interactivo
    if (isNextDisabled) {
      return {
        ...arrowRightStyle,
        color: COLORS.fgDisabled,
        borderColor: COLORS.borderDisabled,
        background: COLORS.bgSecondary,
        opacity: 0.5,
        cursor: 'not-allowed',
      };
    }
    
    // Estado con animación pulse (recuadro discreto)
    if (animateNext && canAdvance) {
      return {
        ...arrowRightStyle,
        color: COLORS.primary,
        background: 'transparent',
        border: `1px solid rgba(45, 212, 191, 0.15)`,
        borderRadius: '6px',
        padding: '8px 6px',
        animation: 'pulse-glow-nav 2.2s ease-in-out infinite',
      };
    }
    
    // Estado normal/hover
    return {
      ...arrowRightStyle,
      color: hoveredRight ? COLORS.primary : COLORS.fgMuted,
      borderColor: hoveredRight ? COLORS.primary : COLORS.border,
      background: hoveredRight ? COLORS.bgTertiary : COLORS.bgSecondary,
      opacity: canGoForward ? 1 : 0,
      pointerEvents: canGoForward ? 'auto' : 'none',
      visibility: canGoForward ? 'visible' : 'hidden',
    };
  };

  const handleNextClick = () => {
    if (!isNextDisabled && onNext) {
      onNext();
    }
  };

  return (
    <>
      {/* Flecha izquierda (paso anterior) */}
      {canGoBack && (
        <button
          type="button"
          data-nav="prev"
          style={getLeftStyle()}
          onClick={onPrev}
          onMouseEnter={() => setHoveredLeft(true)}
          onMouseLeave={() => setHoveredLeft(false)}
          aria-label={`Paso anterior — Ir a ${prevLabel}`}
        >
          <ChevronLeft />
        </button>
      )}

      {/* Flecha derecha (paso siguiente) */}
      {canGoForward && (
        <button
          type="button"
          data-nav="next"
          style={getRightStyle()}
          onClick={handleNextClick}
          onMouseEnter={() => setHoveredRight(true)}
          onMouseLeave={() => setHoveredRight(false)}
          aria-label={isNextDisabled ? 'Completa este paso para continuar' : `Paso siguiente — Ir a ${nextLabel}`}
          aria-disabled={isNextDisabled}
        >
          <ChevronRight />
          
          {/* Tooltip en hover cuando disabled */}
          {isNextDisabled && hoveredRight && (
            <span style={tooltipStyle}>
              Completa este paso para continuar
            </span>
          )}
        </button>
      )}
    </>
  );
};

export default NavArrows;
